/**
 * Query keys.
 *
 * One place, because invalidation is the whole point: a mutation that saves
 * Bindings has to be able to say "and every view of this profile is now stale"
 * without knowing which screens exist. The keys nest the way the API nests, so
 * invalidating `profile(id)` also invalidates that profile's bindings and runs —
 * a prefix match is what makes that work, and it only works if the prefixes are
 * written once.
 *
 * `as const` throughout, so a typo in a key is a type error rather than a cache
 * entry nobody ever reads.
 */

export const keys = {
  /** The signed-in user. Its absence is what "signed out" means. */
  me: ["me"] as const,

  /** The source menu. Declarations, so it changes only when the portal ships. */
  templates: ["templates"] as const,
  /**
   * The same menu read by league, which is the order Add Source asks in.
   *
   * Its own key rather than a selector over `templates`, because it is a
   * different document from a different endpoint — only the server can know
   * which parameter on which template reaches which league. Declarations too,
   * so it changes only when the portal ships.
   */
  leagues: ["templates", "leagues"] as const,
  /**
   * One composition. The parameters are part of the key so that changing the
   * season fetches the addresses that season produces rather than re-showing
   * the previous ones — which is what makes "changing the season recomposes
   * every Feed URL" something a person can see happen.
   */
  templateComposition: (key: string, parameters: unknown) =>
    ["templates", key, "composition", parameters] as const,

  /**
   * The Transfer Portal menu and its profiles. A separate prefix from
   * `profiles`, because they are a different kind of thing with a different
   * endpoint — sharing one would leave whichever screen mounted second reading
   * the other's cached list.
   */
  transferPortalTemplates: ["transfer-portal", "templates"] as const,
  transferPortalComposition: (key: string, parameters: unknown) =>
    ["transfer-portal", "templates", key, "composition", parameters] as const,
  transferPortalProfiles: ["transfer-portal", "profiles"] as const,
  transferPortalProfile: (id: string) => ["transfer-portal", "profiles", id] as const,

  /** The workspace at a glance, one entry per window. */
  dashboard: (days: number, timeZone: string) => ["dashboard", days, timeZone] as const,
  /** One page of the run history, under the Dashboard's prefix. */
  dashboardActivity: (page: number, pageSize: number) => ["dashboard", "activity", page, pageSize] as const,

  profiles: ["profiles"] as const,
  profile: (id: string) => ["profiles", id] as const,
  bindings: (id: string) => ["profiles", id, "bindings"] as const,
  /**
   * Which of a profile's Feeds publish which path (ADR-0061).
   *
   * Under the profile's prefix because it is a reading of the profile's
   * **Feeds** — not of its Bindings, which is the other thing the Fields screen
   * reads and the one a save changes.
   *
   * It therefore refetches whenever `profile(id)` is invalidated, since that
   * key prefix-matches this one. That is correct rather than merely tolerated:
   * a save can add or remove Feeds, and a reading listing a Feed that has gone
   * would offer a disclosure onto a report nothing will fetch.
   */
  publishedFields: (id: string) => ["profiles", id, "published-fields"] as const,
  profileRuns: (id: string) => ["profiles", id, "runs"] as const,

  run: (id: string) => ["runs", id] as const,

  /**
   * One Credential Set, polled while its sign-in runs (ADR-0033).
   *
   * Creating a `login_form` credential returns before the browser has finished
   * with the publisher's login, so the Add Source screen watches this rather
   * than reading an outcome off the create response — which no longer carries
   * one.
   */
  credential: (id: string) => ["credentials", id] as const,

  /**
   * The Stat Dictionary, whole. Also the prefix every filtered view of it
   * hangs off, so one invalidation refreshes them all — which is what lets a
   * statistic added from a field-mapping row appear in the dropdown without
   * re-running discovery.
   */
  stats: ["stats"] as const,
  /**
   * One sport's entries. A separate key rather than the same one, because a
   * filtered list and the whole dictionary are different answers: sharing
   * `stats` between them would leave whichever screen mounted second reading
   * the other's cached list, and offering a football dropdown on a soccer
   * source is the failure the filter exists to prevent.
   */
  statsFor: (sport: string) => ["stats", sport] as const,
  /**
   * The Canonical Position Groups each sport offers, for the *Positions Groups
   * Excluded* control.
   *
   * **Not under `stats`**, which every write to the dictionary invalidates: the
   * groups come from the position vocabulary and the sport's Source Profiles,
   * so adding a statistic cannot change them and refetching them on every save
   * would be a request per keystroke of the add form.
   */
  positionGroups: ["position-groups"] as const,
  /** The raw labels one source published, under that profile's own prefix so a
   *  save to the profile invalidates them together. */
  positionLabels: (id: string) => ["profiles", id, "position-labels"] as const,
  storage: ["storage"] as const,
} as const;

/**
 * How often a screen showing live work re-asks the server.
 *
 * A Run advances on the server whether or not anyone is watching, and there is
 * no push channel, so these are the two polling speeds the portal uses: one for
 * a list where a Run's status changes a row, and a faster one for the Run screen
 * itself, where the phase and the record count are the content.
 */
export const POLL_LIST_MS = 4000;
export const POLL_RUN_MS = 1000;

/** The Run states that will never change again, so polling can stop. */
const SETTLED = ["succeeded", "failed", "skipped", "cancelled"];

export const isSettled = (status: string | undefined): boolean =>
  status !== undefined && SETTLED.includes(status);
